import { Box, Grid } from '@atgfr8/atg-ui-tailwind';
import { InformationOutlineIcon } from '@atgfr8/icons/InformationOutline';
import Popover from '@components/Popover/Popover';
import {
  LtlQuoteViewModel,
  LtlTerminalInfoViewModel,
} from '@store/orders/types';
import { ConfigManager } from '@utils/ConfigManager';
import { formatAmount } from '@utils/utils';
import { isFeatureFlagEnabled } from '@store/featureFlags/store';
import { FeatureFlags } from '@constants/featureFlags';
import { JSX, Show } from 'solid-js';

import { ViewTerminalPopover } from '../../ViewTerminalPopover';

const LABEL_CLASS = 'text-[#00000099]';
const VALUE_CLASS = 'font-semibold text-[#222222]';
const LINE_CLASS =
  'text-sm sm:text-base leading-5 sm:leading-6 text-wrap wrap-break-word';

const DetailLine = (props: {
  label: string;
  value: JSX.Element;
  dataTestId?: string;
}) => (
  <Box class={LINE_CLASS}>
    <span class={LABEL_CLASS}>{props.label}: </span>
    <span class={VALUE_CLASS} data-testid={props.dataTestId}>
      {props.value}
    </span>
  </Box>
);

export const QuoteCarrierDetails = (props: {
  quote: LtlQuoteViewModel;
  showTerminalInfo: boolean;
  carrierNameDataTestId?: string;
  onOpenLiabilityInfo?: (quote: LtlQuoteViewModel) => void;
}) => {
  const isCarrierQuoteExpirationEnabled = isFeatureFlagEnabled(
    FeatureFlags.LtlCarrierQuoteExpirationDisplay,
  )();
  const showCarrierQuoteExpiration = () =>
    ['PricingDynamic', 'PricingVolume', 'Dynamic', 'Volume'].includes(
      props.quote.pricingRequestType ?? '',
    ) &&
    Boolean(props.quote.expirationDate) &&
    isCarrierQuoteExpirationEnabled;
  const getFormattedQuoteExpiration = (): string => {
    const expirationDate: string | null | undefined =
      props.quote.expirationDate;
    if (!expirationDate) {
      return 'Not available';
    }
    return new Date(expirationDate).toLocaleDateString();
  };

  const deliveryDate = () =>
    Boolean(props.quote.estimatedDeliveryDate)
      ? new Date(
          props.quote.estimatedDeliveryDate as string,
        ).toLocaleDateString()
      : '--';

  const carrierNameTestId = () =>
    props.carrierNameDataTestId ??
    `quoteCarrierDetails-carrierName-quote-${props.quote.id}`;

  return (
    <Box class="flex flex-col h-full w-full p-2">
      <Grid container spacing={2} alignItems="stretch">
        {/* Column 1: carrier identity */}
        <Grid item size={{ xs: 12, sm: 6, md: 4 }}>
          <Box
            class="font-semibold text-base sm:text-lg text-wrap wrap-break-word"
            data-testid={carrierNameTestId()}
          >
            {props.quote.carrierName}
          </Box>
          <DetailLine
            label="Carrier Quote ID"
            value={props.quote.carrierQuoteId ?? '--'}
          />
          <Box class="flex justify-center sm:justify-start mt-3">
            <img
              src={`${ConfigManager.apiUrl}/api/carrier/logo/${props.quote.carrierId}`}
              alt="carrier logo"
              class="max-h-[120px] max-w-[150px] object-fill"
            />
          </Box>
        </Grid>

        {/* Column 2: transit, delivery, service type */}
        <Grid item size={{ xs: 12, sm: 6, md: 4 }}>
          <Box class="flex flex-col gap-2">
            <DetailLine
              label="Est. Transit Days"
              value={props.quote.transitDays ?? '--'}
            />
            <DetailLine label="Est. Delivery Date" value={deliveryDate()} />
            <DetailLine
              label="Service Type"
              value={props.quote.serviceTypeDisplayName ?? '--'}
            />
            <Show when={showCarrierQuoteExpiration()}>
              <DetailLine
                label="Quote Expires"
                value={getFormattedQuoteExpiration()}
                dataTestId="quoteCarrierDetails-carrierQuoteExpiration"
              />
            </Show>
          </Box>
        </Grid>

        {/* Column 3: terminal info, movement type, liability */}
        <Grid item size={{ xs: 12, sm: 12, md: 4 }}>
          <Box class="flex flex-col gap-2">
            <Show when={props.showTerminalInfo}>
              <Box class={LINE_CLASS}>
                <Popover
                  anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
                  transformOrigin={{ vertical: 'top', horizontal: 'center' }}
                  sxProps={{ padding: '0' }}
                  eleName={
                    <span
                      class="inline-flex items-center text-[#307eac] cursor-pointer"
                      data-testid={`quoteCarrierDetails-terminalInfo-quote-${props.quote.id}`}
                    >
                      <InformationOutlineIcon size={16} color="#026ea1" />
                      <span class="ml-1">Terminal Info</span>
                    </span>
                  }
                >
                  <ViewTerminalPopover
                    terminalInfo={
                      props.quote.terminalInfo as LtlTerminalInfoViewModel
                    }
                    quoteId={String(props.quote.id)}
                  />
                </Popover>
              </Box>
            </Show>
            <DetailLine
              label="Movement Type"
              value={props.quote.movementType ?? '--'}
            />
            <Box class={LINE_CLASS}>
              <Show
                when={props.onOpenLiabilityInfo}
                fallback={
                  <span class={LABEL_CLASS}>Max Carrier Liability</span>
                }
              >
                <button
                  type="button"
                  class={`inline-flex items-center gap-1 rounded p-0.5 cursor-pointer transition-colors hover:bg-[#E8F3FA] focus:outline-none focus:ring-2 focus:ring-[#9ECBE3] ${LABEL_CLASS}`}
                  onClick={() => props.onOpenLiabilityInfo?.(props.quote)}
                  data-testid={`quoteCarrierDetails-liabilityInfo-quote-${props.quote.id}`}
                >
                  <InformationOutlineIcon size={16} color="#026ea1" />
                  <span>Max Carrier Liability</span>
                </button>
              </Show>
              <Box class="flex flex-wrap gap-x-4">
                <span>
                  <span class={LABEL_CLASS}>New: </span>
                  <span class={VALUE_CLASS}>
                    {formatAmount(props.quote.maxNewLiability)}
                  </span>
                </span>
                <span>
                  <span class={LABEL_CLASS}>Used: </span>
                  <span class={VALUE_CLASS}>
                    {formatAmount(props.quote.maxUsedLiability)}
                  </span>
                </span>
              </Box>
            </Box>
          </Box>
        </Grid>
      </Grid>

      <Show when={props.quote.carrierNotes?.trim()}>
        <Box class="flex items-start mt-3 max-w-full">
          <span
            class="text-xs sm:text-sm font-bold text-[#1B4960] shrink-0 mr-2"
            data-testid="quoteCarrierDetails-carrierNote"
          >
            Carrier Note
          </span>
          <span class="text-xs sm:text-sm text-[#00000099] break-words leading-relaxed flex-1 min-w-0">
            {`"${props.quote.carrierNotes}"`}
          </span>
        </Box>
      </Show>
    </Box>
  );
};
